import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';

export function IronManThanosGame() {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [gameState, setGameState] = useState<'idle' | 'playing' | 'victory' | 'snapped'>('idle');
  const [ironManY, setIronManY] = useState(200);
  const [ironManX, setIronManX] = useState(80);

  // Dynamic Animated Thanos Positions & States
  const [thanosY, setThanosY] = useState(100);
  const [thanosX, setThanosX] = useState(600);
  const [thanosHealth, setThanosHealth] = useState(100);
  const [thanosIsHit, setThanosIsHit] = useState(false);

  const [snapTimer, setSnapTimer] = useState(15);
  const [lasers, setLasers] = useState<{ id: number; x: number; y: number }[]>([]);

  const audioCtxRef = useRef<AudioContext | null>(null);

  // Web Audio Synthesizer
  const playSound = (type: 'fly' | 'laser' | 'hit' | 'victory') => {
    if (!audioCtxRef.current) {
      const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (AudioCtx) audioCtxRef.current = new AudioCtx();
    }
    if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume();
    }
    const ctx = audioCtxRef.current;
    if (!ctx) return;

    if (type === 'laser') {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(800, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 0.15);
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.15);
    } else if (type === 'hit') {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'square';
      osc.frequency.setValueAtTime(200, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(50, ctx.currentTime + 0.1);
      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.1);
    } else if (type === 'victory') {
      const notes = [523.25, 659.25, 783.99, 1046.5];
      notes.forEach((freq, idx) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.15, ctx.currentTime + idx * 0.12);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + idx * 0.12 + 0.6);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + idx * 0.12);
        osc.stop(ctx.currentTime + idx * 0.12 + 0.6);
      });
    }
  };

  // Keyboard controls: SPACEBAR to fly, RIGHT CLICK or ENTER to fire laser
  useEffect(() => {
    if (!isFullScreen || gameState !== 'playing') return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        e.preventDefault();
        setIronManY((prev) => Math.max(20, prev - 45));
        setIronManX((prev) => Math.min(750, prev + 25));
        playSound('fly');
      } else if (e.code === 'Enter' || e.code === 'KeyF') {
        fireLaser();
      }
    };

    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      fireLaser();
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('contextmenu', handleContextMenu);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [isFullScreen, gameState, ironManX, ironManY]);

  const fireLaser = () => {
    if (gameState !== 'playing') return;
    playSound('laser');
    setLasers((prev) => [...prev, { id: Date.now(), x: ironManX + 120, y: ironManY + 30 }]);

    // Check hit on moving Thanos
    if (ironManX > 400 || Math.abs(ironManX - thanosX) < 250) {
      playSound('hit');
      setThanosIsHit(true);
      setTimeout(() => setThanosIsHit(false), 200);

      setThanosHealth((prev) => {
        const next = Math.max(0, prev - 25);
        if (next <= 0) {
          setGameState('victory');
          playSound('victory');
        }
        return next;
      });
    }
  };

  // Dynamic Thanos Movement & Game Loop
  useEffect(() => {
    if (!isFullScreen || gameState !== 'playing') return;

    let step = 0;

    const timer = setInterval(() => {
      setSnapTimer((prev) => {
        if (prev <= 1) {
          setGameState('snapped');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Continuous 60fps Thanos Hover & Fly Loop
    const physics = setInterval(() => {
      step += 0.08;

      // Thanos smooth sine wave floating motion (up & down, forward & backward)
      setThanosY(120 + Math.sin(step) * 50);
      setThanosX(550 + Math.cos(step * 0.7) * 40);

      // Iron Man gravity
      setIronManY((prev) => Math.min(360, prev + 5));

      // Move Arc Lasers
      setLasers((prev) =>
        prev
          .map((l) => ({ ...l, x: l.x + 30 }))
          .filter((l) => l.x < 1100)
      );
    }, 40);

    return () => {
      clearInterval(timer);
      clearInterval(physics);
    };
  }, [isFullScreen, gameState]);

  const startGame = () => {
    setIsFullScreen(true);
    setIronManX(80);
    setIronManY(200);
    setThanosY(120);
    setThanosX(550);
    setThanosHealth(100);
    setSnapTimer(15);
    setLasers([]);
    setGameState('playing');
  };

  return (
    <>
      {/* In-Page Launcher Button */}
      <div className="relative z-30 my-6 flex flex-col items-center justify-center text-center">
        <button
          onClick={startGame}
          className="group relative flex items-center gap-3 rounded-full border-2 border-amber-400 bg-gradient-to-r from-red-600 via-amber-500 to-red-600 px-8 py-4 text-sm font-black uppercase text-white shadow-[0_0_40px_rgba(245,158,11,0.6)] backdrop-blur-xl transition-all hover:scale-110 active:scale-95"
        >
          <span className="text-xl">🚀</span>
          <span>LAUNCH FULL-SCREEN IRON MAN VS THANOS GAME</span>
        </button>
        <p className="text-xs font-bold text-sky-200 mt-2">
          Tap to enter Full-Screen Arcade Mode & Rescue the Birthday Cake!
        </p>
      </div>

      {/* Full-Screen AAA Game Overlay */}
      <AnimatePresence>
        {isFullScreen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 z-50 flex flex-col justify-between bg-slate-950 p-4 md:p-8 text-center select-none overflow-hidden"
          >
            {/* Top HUD Bar */}
            <div className="flex items-center justify-between rounded-2xl border border-sky-400/40 bg-black/80 px-6 py-3 text-xs font-bold shadow-xl">
              <div className="flex items-center gap-2">
                <span className="text-xl">⚡</span>
                <span className="text-sky-300 font-extrabold uppercase tracking-widest">
                  IRON MAN BIRTHDAY RESCUE
                </span>
              </div>

              {gameState === 'playing' && (
                <div className="flex items-center gap-6">
                  <div className="text-slate-300 hidden sm:block">
                    SPACEBAR: <span className="text-amber-300">FLY</span> | RIGHT-CLICK: <span className="text-red-400">LASER BLAST</span>
                  </div>
                  <div className="rounded-xl border border-red-500 bg-red-950/80 px-4 py-1.5 text-red-300 font-black">
                    THANOS SNAP: {snapTimer}s
                  </div>
                </div>
              )}

              <button
                onClick={() => setIsFullScreen(false)}
                className="rounded-full border border-slate-700 bg-slate-800 px-4 py-1.5 text-xs font-bold text-slate-300 hover:bg-slate-700"
              >
                ✕ EXIT GAME
              </button>
            </div>

            {/* Middle Full-Screen Arena */}
            <div className="relative my-4 flex-1 w-full rounded-3xl border-2 border-cyan-500/40 bg-slate-950 overflow-hidden isolate shadow-2xl">
              <img
                src="/assets/birthday/stark_ironman_cosmic_arena.png"
                alt="Stark Iron Man Cosmic Arena Background"
                className="absolute inset-0 w-full h-full object-cover opacity-90 pointer-events-none"
              />

              {/* Victory Screen */}
              {gameState === 'victory' && (
                <motion.div initial={{ scale: 0.8 }} animate={{ scale: 1 }} className="absolute inset-0 z-40 flex flex-col items-center justify-center p-6 text-center bg-black/90 backdrop-blur-2xl">
                  <h2 className="text-4xl md:text-5xl font-black uppercase text-amber-300 tracking-wider">
                    🎉 THANOS DEFEATED! CAKE RESCUED! 🎉
                  </h2>
                  <p className="text-sm font-bold text-sky-200 mt-2 max-w-lg">
                    Iron Man successfully delivered the Birthday Cake to the party stage! You earned 100% Victory!
                  </p>
                  <img src="/assets/birthday/baby_ironman_cake_powerup.svg" alt="Cake" className="w-80 h-auto my-4 drop-shadow-[0_0_30px_rgba(255,215,0,0.8)]" />
                  <div className="flex gap-4">
                    <button
                      onClick={startGame}
                      className="rounded-full bg-amber-400 px-8 py-3.5 text-xs font-black uppercase text-slate-950 shadow-lg"
                    >
                      🔄 REPLAY GAME
                    </button>
                    <button
                      onClick={() => setIsFullScreen(false)}
                      className="rounded-full bg-sky-500 px-8 py-3.5 text-xs font-black uppercase text-white shadow-lg"
                    >
                      📜 OPEN CELEBRATION INVITATION
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Snapped Screen */}
              {gameState === 'snapped' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="absolute inset-0 z-40 flex flex-col items-center justify-center p-6 text-center bg-purple-950/95 backdrop-blur-2xl">
                  <h2 className="text-4xl font-black uppercase text-red-400">💨 THANOS SNAPPED THE CAKE TO DUST!</h2>
                  <p className="text-xs text-purple-200 mt-2">The Snap Timer hit zero! Try flying faster and blasting Thanos!</p>
                  <button
                    onClick={startGame}
                    className="mt-6 rounded-full bg-amber-400 px-8 py-3.5 text-xs font-black uppercase text-slate-950 shadow-lg"
                  >
                    🔄 RETRY MISSION
                  </button>
                </motion.div>
              )}

              {/* Flying Iron Man */}
              {gameState === 'playing' && (
                <div
                  className="absolute transition-all duration-75 pointer-events-none"
                  style={{ left: `${ironManX}px`, top: `${ironManY}px` }}
                >
                  <img src="/assets/birthday/ironman_flying_hero.png" alt="Iron Man Flying Armor" className="w-44 h-auto drop-shadow-[0_0_20px_rgba(245,158,11,0.9)]" />
                </div>
              )}

              {/* Fired Lasers */}
              {gameState === 'playing' &&
                lasers.map((l) => (
                  <div
                    key={l.id}
                    className="absolute h-5 w-16 rounded-full bg-gradient-to-r from-sky-400 via-white to-amber-300 shadow-[0_0_20px_#38bdf8] pointer-events-none"
                    style={{ left: `${l.x}px`, top: `${l.y}px` }}
                  />
                ))}

              {/* DYNAMIC MOVING & FLOATING THANOS BOSS */}
              {gameState === 'playing' && (
                <motion.div
                  className="absolute flex items-center gap-4 pointer-events-none transition-transform"
                  style={{
                    left: `${thanosX}px`,
                    top: `${thanosY}px`,
                    filter: thanosIsHit ? 'brightness(1.8) drop-shadow(0 0 35px #ef4444)' : 'none',
                  }}
                  animate={{
                    scale: thanosIsHit ? [1, 1.15, 1] : 1,
                  }}
                  transition={{ duration: 0.15 }}
                >
                  {/* Thanos Boss & Health Bar */}
                  <div className="relative flex flex-col items-center">
                    <div className="w-48 h-3 rounded-full bg-slate-800 border border-red-500 overflow-hidden mb-2">
                      <div className="h-full bg-red-500 transition-all" style={{ width: `${thanosHealth}%` }} />
                    </div>
                    <img src="/assets/birthday/thanos_gauntlet_boss.svg" alt="Thanos Boss" className="w-56 h-auto drop-shadow-[0_0_30px_rgba(168,85,247,0.9)] animate-pulse" />
                  </div>

                  {/* Target Birthday Cake */}
                  <div className="flex flex-col items-center">
                    <span className="text-xs font-bold text-amber-300 animate-pulse mb-1">🎂 TARGET CAKE</span>
                    <img src="/assets/birthday/baby_ironman_cake_powerup.svg" alt="Cake" className="w-36 h-auto opacity-90" />
                  </div>
                </motion.div>
              )}
            </div>

            {/* Bottom Touch Controls */}
            {gameState === 'playing' && (
              <div className="flex items-center justify-center gap-6">
                <button
                  onClick={() => {
                    setIronManY((prev) => Math.max(20, prev - 45));
                    setIronManX((prev) => Math.min(750, prev + 25));
                    playSound('fly');
                  }}
                  className="flex-1 rounded-2xl border-2 border-sky-400 bg-sky-950/90 py-5 text-sm font-black uppercase text-sky-200 shadow-2xl active:scale-95"
                >
                  🚀 TAP / HOLD TO FLY & BOOST
                </button>
                <button
                  onClick={fireLaser}
                  className="flex-1 rounded-2xl border-2 border-red-500 bg-red-950/90 py-5 text-sm font-black uppercase text-red-200 shadow-2xl active:scale-95"
                >
                  💥 TAP TO BLAST ARC LASER!
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
